/**
 * PatiAccessories — Hat & Necklace SVG layers for Pati Asistan.
 *
 * Design goals:
 *   • Tiny, additive layers that paint *over* CatAvatar inside the same viewBox
 *     (0 0 100 100). Each render function returns a <g> ready to drop in.
 *   • All accessories are pure SVG primitives — no images, no extra network.
 *   • Hats support a `subColor` choice (Turkish football team palettes).
 *
 * Catalog ids are stable strings — persisted in localStorage by PatiAsistan.
 */
import { useEffect, useState } from "react";

const STORAGE_KEY = "patiAsistan.accessories.v1";

/* ─────────────── Hat palettes — generic two-color combinations.
 * IMPORTANT: These are trademark-safe. We never reference team names,
 * crests, or stripes; only the raw color pairing. Labels mention the
 * colors themselves, not any club.
 */
export const HAT_PALETTES = {
  "combo-1": { primary: "#fde047", secondary: "#1e3a8a", labelTr: "Sarı + Lacivert",   labelEn: "Yellow + Navy" },
  "combo-2": { primary: "#fde047", secondary: "#b91c1c", labelTr: "Sarı + Kırmızı",   labelEn: "Yellow + Red" },
  "combo-3": { primary: "#111827", secondary: "#f8fafc", labelTr: "Siyah + Beyaz",    labelEn: "Black + White" },
  "combo-4": { primary: "#7f1d1d", secondary: "#1e3a8a", labelTr: "Bordo + Mavi",     labelEn: "Maroon + Blue" },
  "combo-5": { primary: "#ea580c", secondary: "#fff7ed", labelTr: "Klasik Turuncu",   labelEn: "Classic Orange" },
};

/* ─────────────── Catalog ─────────────── */
export const HATS = [
  { id: "none",   labelTr: "Yok",          labelEn: "None",         rarity: "free", supportsPalette: false },
  { id: "beanie", labelTr: "Bere",         labelEn: "Beanie",       rarity: "free", supportsPalette: true,  defaultPalette: "combo-1" },
  { id: "scarf",  labelTr: "Atkı + Bere",  labelEn: "Beanie+Scarf", rarity: "free", supportsPalette: true,  defaultPalette: "combo-2" },
  { id: "crown",  labelTr: "Taç",          labelEn: "Crown",        rarity: "rare", supportsPalette: false },
];

export const NECKLACES = [
  { id: "none",     labelTr: "Yok",                labelEn: "None",            rarity: "free" },
  { id: "collar",   labelTr: "Klasik Tasma",       labelEn: "Basic Collar",    rarity: "free" },
  { id: "diamond",  labelTr: "Pırlanta Kolye",     labelEn: "Diamond Necklace",rarity: "rare" },
  { id: "bell",     labelTr: "Altın Çıngırak",     labelEn: "Golden Bell",     rarity: "rare" },
];

/* ─────────────── Hair (fur) styles — Premium only ─────────────── */
export const HAIRSTYLES = [
  { id: "none",     labelTr: "Yok",             labelEn: "None",            premium: false },
  { id: "tuft",     labelTr: "Kabarık Tutam",   labelEn: "Fluffy Tuft",     premium: true },
  { id: "mohawk",   labelTr: "Mohikan",         labelEn: "Mohawk",          premium: true },
  { id: "sideswept",labelTr: "Yan Kâkül",       labelEn: "Side-swept Bangs",premium: true },
  { id: "topknot",  labelTr: "Lokmalı Topuz",   labelEn: "Curly Topknot",   premium: true },
];

export const HAIR_COLORS = {
  white:   { hex: "#ffffff", shade: "#e2e8f0", labelTr: "Beyaz",     labelEn: "White" },
  cream:   { hex: "#fff5e3", shade: "#f0d8a4", labelTr: "Krem",      labelEn: "Cream" },
  ginger:  { hex: "#ffb04a", shade: "#d56b18", labelTr: "Zencefil",  labelEn: "Ginger" },
  black:   { hex: "#1f2937", shade: "#0a0a0a", labelTr: "Siyah",     labelEn: "Black" },
  pink:    { hex: "#fbcfe8", shade: "#ec4899", labelTr: "Pembe",     labelEn: "Pink" },
  blue:    { hex: "#bae6fd", shade: "#0ea5e9", labelTr: "Mavi",      labelEn: "Blue" },
  purple:  { hex: "#e9d5ff", shade: "#9333ea", labelTr: "Mor",       labelEn: "Purple" },
};

/* ─────────────── Persistence hook ─────────────── */
const DEFAULT_STATE = {
  hat: "none",
  hatPalette: "combo-1",
  necklace: "none",
  hair: "none",
  hairColor: "white",
};

export function useAccessories() {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return DEFAULT_STATE;
      return { ...DEFAULT_STATE, ...JSON.parse(raw) };
    } catch {
      return DEFAULT_STATE;
    }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
  }, [state]);
  const setHat = (id) => setState((s) => {
    const hat = HATS.find((h) => h.id === id);
    return { ...s, hat: id, hatPalette: hat?.supportsPalette ? (s.hatPalette || hat.defaultPalette) : s.hatPalette };
  });
  const setHatPalette = (key) => setState((s) => ({ ...s, hatPalette: key }));
  const setNecklace = (id) => setState((s) => ({ ...s, necklace: id }));
  const setHair = (id) => setState((s) => ({ ...s, hair: id }));
  const setHairColor = (key) => setState((s) => ({ ...s, hairColor: key }));
  return { ...state, setHat, setHatPalette, setNecklace, setHair, setHairColor };
}

/* ─────────────── Hat renderers ─────────────── */
// Beanie: a cosy knit cap sitting on top of the head, with a folded brim and pom-pom.
function HatBeanie({ palette }) {
  const p = HAT_PALETTES[palette] || HAT_PALETTES["combo-1"];
  return (
    <g data-testid="pati-hat-beanie">
      {/* Main beanie dome — sits between the ears */}
      <path d="M28 24 Q50 4 72 24 L72 30 Q50 22 28 30 Z" fill={p.primary} />
      {/* Knit ribbing stripe */}
      <path d="M30 26 Q50 18 70 26" stroke={p.secondary} strokeWidth="1.4" fill="none" opacity="0.85" />
      <path d="M30 28 Q50 22 70 28" stroke={p.secondary} strokeWidth="1" fill="none" opacity="0.55" />
      {/* Folded brim */}
      <path d="M27 27 Q50 19 73 27 L73 33 Q50 27 27 33 Z" fill={p.secondary} />
      {/* Brim seam highlight */}
      <path d="M28 30 Q50 24 72 30" stroke="rgba(255,255,255,0.5)" strokeWidth="0.6" fill="none" />
      {/* Pom-pom */}
      <circle cx="50" cy="8" r="4.2" fill={p.secondary} />
      <circle cx="48.5" cy="6.5" r="1.4" fill="rgba(255,255,255,0.6)" />
    </g>
  );
}

// Beanie + scarf — adds a knit scarf wrapped around the neck.
function HatScarf({ palette }) {
  const p = HAT_PALETTES[palette] || HAT_PALETTES["combo-2"];
  return (
    <g data-testid="pati-hat-scarf">
      <HatBeanie palette={palette} />
      {/* Scarf wrap (under chin, over body collar) */}
      <path d="M30 62 Q50 70 70 62 L72 68 Q50 76 28 68 Z" fill={p.primary} />
      {/* Scarf knot */}
      <ellipse cx="62" cy="68" rx="4" ry="3" fill={p.secondary} />
      <path d="M64 70 L70 78 L66 79 Z" fill={p.primary} />
      <path d="M66 71 L72 80 L68 81 Z" fill={p.secondary} />
      {/* Knit stripes */}
      <path d="M32 64 Q50 70 68 64" stroke={p.secondary} strokeWidth="0.8" fill="none" opacity="0.7" />
    </g>
  );
}

// Crown — small gold tiara
function HatCrown() {
  return (
    <g data-testid="pati-hat-crown">
      {/* Base band */}
      <rect x="32" y="22" width="36" height="5" rx="1" fill="#facc15" stroke="#a16207" strokeWidth="0.5" />
      {/* Spikes */}
      <path d="M32 22 L36 12 L40 22 L46 10 L50 22 L54 10 L60 22 L64 12 L68 22 Z"
            fill="#fde047" stroke="#a16207" strokeWidth="0.6" strokeLinejoin="round" />
      {/* Gems */}
      <circle cx="36" cy="13" r="1.4" fill="#ef4444" />
      <circle cx="50" cy="11" r="1.6" fill="#3b82f6" />
      <circle cx="64" cy="13" r="1.4" fill="#10b981" />
      {/* Shine */}
      <path d="M34 23 L66 23" stroke="rgba(255,255,255,0.65)" strokeWidth="0.6" />
    </g>
  );
}

export function HatLayer({ hat, palette }) {
  if (!hat || hat === "none") return null;
  if (hat === "beanie") return <HatBeanie palette={palette} />;
  if (hat === "scarf")  return <HatScarf palette={palette} />;
  if (hat === "crown")  return <HatCrown />;
  return null;
}

/* ─────────────── Necklace renderers ─────────────── */
// Sits around the neck zone (~y=62-66) just under the chin.
function CollarBasic() {
  return (
    <g data-testid="pati-necklace-collar">
      {/* Strap */}
      <path d="M34 64 Q50 70 66 64 L66 67 Q50 73 34 67 Z" fill="#b91c1c" />
      {/* Stitch line */}
      <path d="M35 66 Q50 71 65 66" stroke="rgba(255,255,255,0.4)" strokeWidth="0.4" fill="none" strokeDasharray="1.2 1" />
      {/* Tag */}
      <circle cx="50" cy="71" r="2.6" fill="#fbbf24" stroke="#a16207" strokeWidth="0.4" />
      <circle cx="49.3" cy="70.3" r="0.7" fill="rgba(255,255,255,0.75)" />
    </g>
  );
}

function CollarDiamond() {
  return (
    <g data-testid="pati-necklace-diamond">
      {/* Silver chain */}
      <path d="M34 64 Q50 69 66 64" stroke="#cbd5e1" strokeWidth="0.9" fill="none" />
      <path d="M34 65 Q50 70 66 65" stroke="#94a3b8" strokeWidth="0.6" fill="none" opacity="0.7" />
      {/* Pendant setting */}
      <circle cx="50" cy="71" r="2.3" fill="#e2e8f0" stroke="#64748b" strokeWidth="0.4" />
      {/* Diamond (faceted) */}
      <path d="M50 68.6 L52.2 71 L50 73.4 L47.8 71 Z" fill="#bae6fd" stroke="#0ea5e9" strokeWidth="0.4" />
      <path d="M50 68.6 L52.2 71 L50 71 Z" fill="#e0f2fe" />
      {/* Sparkle */}
      <circle cx="50" cy="70" r="0.45" fill="#fff" className="pati-sparkle" />
    </g>
  );
}

function CollarBell() {
  return (
    <g data-testid="pati-necklace-bell">
      {/* Velvet strap (deep red) */}
      <path d="M34 64 Q50 70 66 64 L66 67 Q50 73 34 67 Z" fill="#7f1d1d" />
      <path d="M35 65.5 Q50 71 65 65.5" stroke="#fcd34d" strokeWidth="0.45" fill="none" />
      {/* Bell — gold */}
      <path d="M47 69 Q50 67 53 69 L53 72 Q53 74.5 50 75 Q47 74.5 47 72 Z" fill="#facc15" stroke="#a16207" strokeWidth="0.4" />
      {/* Bell slit */}
      <line x1="50" y1="72" x2="50" y2="74.5" stroke="#a16207" strokeWidth="0.5" />
      {/* Bell highlight */}
      <path d="M48 69.5 Q49 68.5 50 69" stroke="#fef3c7" strokeWidth="0.5" fill="none" />
      {/* Tiny clapper dot */}
      <circle cx="50" cy="75.2" r="0.55" fill="#a16207" />
    </g>
  );
}

export function NecklaceLayer({ necklace }) {
  if (!necklace || necklace === "none") return null;
  if (necklace === "collar")  return <CollarBasic />;
  if (necklace === "diamond") return <CollarDiamond />;
  if (necklace === "bell")    return <CollarBell />;
  return null;
}

/* ─────────────── Hair renderers ───────────────
 * Each hair piece is wrapped in pati-fur (CSS shimmer) + patiFurWind filter
 * so it ripples like the rest of the cat's coat. Color is independent of body.
 */
function FurStrand({ x1, y1, x2, y2, color, w = 0.9 }) {
  return <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={w} strokeLinecap="round" />;
}

function HairTuft({ color, shade }) {
  // Small fluffy tuft in the middle of the forehead — 7 wisps fanning up.
  return (
    <g filter="url(#patiFurWind)" className="pati-fur" data-testid="pati-hair-tuft">
      <FurStrand x1="46" y1="22" x2="44" y2="14" color={shade} w={1.2} />
      <FurStrand x1="48" y1="21" x2="47" y2="11" color={color} w={1.4} />
      <FurStrand x1="50" y1="20" x2="50" y2="9"  color={color} w={1.5} />
      <FurStrand x1="52" y1="21" x2="53" y2="11" color={color} w={1.4} />
      <FurStrand x1="54" y1="22" x2="56" y2="14" color={shade} w={1.2} />
      <FurStrand x1="47" y1="22" x2="46" y2="17" color={color} w={1.0} />
      <FurStrand x1="53" y1="22" x2="54" y2="17" color={color} w={1.0} />
      {/* Tip highlight */}
      <circle cx="50" cy="9.5" r="0.7" fill={color} opacity="0.9" />
    </g>
  );
}

function HairMohawk({ color, shade }) {
  // Vertical fur strip from forehead to crown — between the ears.
  // Tall central spikes flanked by shorter wisps.
  return (
    <g filter="url(#patiFurWind)" className="pati-fur" data-testid="pati-hair-mohawk">
      {/* Base ridge along the scalp */}
      <path d="M44 22 Q50 18 56 22 L55 26 Q50 24 45 26 Z" fill={shade} opacity="0.85" />
      {/* Vertical fur spikes */}
      <FurStrand x1="46" y1="22" x2="45" y2="10" color={shade} w={1.4} />
      <FurStrand x1="47.5" y1="21" x2="47" y2="6"  color={color} w={1.6} />
      <FurStrand x1="49" y1="20" x2="49" y2="3"  color={color} w={1.8} />
      <FurStrand x1="50.5" y1="20" x2="51" y2="2" color={color} w={1.8} />
      <FurStrand x1="52" y1="21" x2="53" y2="6"  color={color} w={1.6} />
      <FurStrand x1="54" y1="22" x2="55" y2="10" color={shade} w={1.4} />
      {/* Side wisps */}
      <FurStrand x1="44" y1="24" x2="42" y2="16" color={shade} w={0.9} />
      <FurStrand x1="56" y1="24" x2="58" y2="16" color={shade} w={0.9} />
      {/* Highlight tip on the tallest spike */}
      <circle cx="50.5" cy="2.5" r="0.8" fill={color} />
    </g>
  );
}

function HairSideSwept({ color, shade }) {
  // Curved bang sweeping from upper-left across the forehead to the right.
  return (
    <g filter="url(#patiFurWind)" className="pati-fur" data-testid="pati-hair-sideswept">
      {/* Main bang body — a swooping shape */}
      <path
        d="M30 22 Q34 12 50 16 Q62 19 64 28 Q56 22 46 24 Q38 26 32 30 Z"
        fill={color}
      />
      {/* Bang shading */}
      <path
        d="M32 24 Q40 16 52 18 Q58 19 60 24"
        stroke={shade}
        strokeWidth="0.8"
        fill="none"
        opacity="0.65"
      />
      {/* Wispy tip */}
      <FurStrand x1="64" y1="27" x2="68" y2="32" color={shade} w={1.1} />
      <FurStrand x1="32" y1="22" x2="28" y2="16" color={shade} w={1.0} />
      <FurStrand x1="38" y1="18" x2="36" y2="11" color={color} w={1.0} />
      <FurStrand x1="48" y1="16" x2="48" y2="9"  color={color} w={1.0} />
      {/* Shine streak */}
      <path d="M36 20 Q46 17 56 20" stroke="rgba(255,255,255,0.45)" strokeWidth="0.5" fill="none" />
    </g>
  );
}

function HairTopknot({ color, shade }) {
  // Small curly bun atop the head — playful, with stray curls.
  return (
    <g filter="url(#patiFurWind)" className="pati-fur" data-testid="pati-hair-topknot">
      {/* Bun base */}
      <ellipse cx="50" cy="14" rx="6" ry="5" fill={color} />
      <ellipse cx="50" cy="14" rx="6" ry="5" fill={shade} opacity="0.25" />
      {/* Curls on the bun */}
      <circle cx="46" cy="12" r="2" fill={color} />
      <circle cx="50" cy="10" r="2.3" fill={color} />
      <circle cx="54" cy="12" r="2" fill={color} />
      <circle cx="48" cy="15" r="1.6" fill={color} />
      <circle cx="52" cy="15" r="1.6" fill={color} />
      {/* Shading dots */}
      <circle cx="50" cy="10" r="0.6" fill={shade} opacity="0.55" />
      <circle cx="46.5" cy="12.5" r="0.5" fill={shade} opacity="0.5" />
      <circle cx="53.5" cy="12.5" r="0.5" fill={shade} opacity="0.5" />
      {/* Stray strand */}
      <path d="M50 19 q-1 4 -3 6" stroke={shade} strokeWidth="0.9" fill="none" strokeLinecap="round" />
      <path d="M50 19 q1 4 3 6" stroke={shade} strokeWidth="0.9" fill="none" strokeLinecap="round" />
      {/* Highlight */}
      <ellipse cx="48" cy="11.5" rx="1.4" ry="0.9" fill="#ffffff" opacity="0.55" />
    </g>
  );
}

export function HairLayer({ hair, colorKey }) {
  if (!hair || hair === "none") return null;
  const c = HAIR_COLORS[colorKey] || HAIR_COLORS.white;
  if (hair === "tuft")      return <HairTuft     color={c.hex} shade={c.shade} />;
  if (hair === "mohawk")    return <HairMohawk   color={c.hex} shade={c.shade} />;
  if (hair === "sideswept") return <HairSideSwept color={c.hex} shade={c.shade} />;
  if (hair === "topknot")   return <HairTopknot  color={c.hex} shade={c.shade} />;
  return null;
}

/* ─────────────── Preview swatch (tiny, used in cards) ─────────────── */
export function AccessoryPreview({ type, id, palette, hairColor }) {
  return (
    <svg viewBox="0 0 100 100" className="w-12 h-12" aria-hidden>
      <defs>
        {/* Local copy of patiFurWind so hair previews ripple even outside the main avatar */}
        <filter id="patiFurWind" x="-10%" y="-10%" width="120%" height="120%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9 0.6" numOctaves="2" seed="3" result="t">
            <animate attributeName="baseFrequency" dur="6s" values="0.9 0.6;1.2 0.9;0.9 0.6" repeatCount="indefinite" />
          </feTurbulence>
          <feDisplacementMap in="SourceGraphic" in2="t" scale="1.4" xChannelSelector="R" yChannelSelector="G" />
        </filter>
      </defs>
      {/* Mini stand-in head */}
      <circle cx="50" cy="50" r="22" fill="#ffd88a" />
      <path d="M30 32 L26 18 L40 30 Z" fill="#ff9d3d" />
      <path d="M70 32 L74 18 L60 30 Z" fill="#ff9d3d" />
      <ellipse cx="44" cy="50" rx="2" ry="3" fill="#0a0a0a" />
      <ellipse cx="56" cy="50" rx="2" ry="3" fill="#0a0a0a" />
      <g transform="translate(0, 8)">
        {type === "hair" && <HairLayer hair={id} colorKey={hairColor} />}
        {type === "hat" && <HatLayer hat={id} palette={palette} />}
        {type === "necklace" && <NecklaceLayer necklace={id} />}
      </g>
    </svg>
  );
}
